import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Settings,
} from 'lucide-react';
import { loadList, exportList } from 'shared/api/requests';
import { useLayout } from 'shared/contexts/LayoutContext';
import { useStateContext } from 'shared/contexts/ContextProvider';
import Tooltip from 'shared/ui/Tooltip';

const ColumnToggler = ({ columnStates, setColumnStates }) => {
  const handleToggle = (key) => {
    setColumnStates((prev) =>
      prev.map((col) => (col.key === key ? { ...col, hide: !col.hide } : col))
    );
  };

  return (
    <div className='bg-white shadow-lg border border-gray-200 rounded-lg p-4 min-w-[200px]'>
      {columnStates.map(({ key, label, hide }) => (
        <div key={key} className='flex items-center space-x-2 py-1'>
          <input
            type='checkbox'
            checked={!hide}
            onChange={() => handleToggle(key)}
          />
          <label className='text-gray-700'>{label}</label>
        </div>
      ))}
    </div>
  );
};

const ActionsMenu = ({ actions, loading }) => {
  return (
    <div className='bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[200px]'>
      {actions.map((action, index) => (
        <React.Fragment key={action.key}>
          <button
            onClick={action.action}
            disabled={loading}
            className='w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
          >
            {action.label}
          </button>
          {index < actions.length - 1 && <div className='border-t border-gray-100' />}
        </React.Fragment>
      ))}
    </div>
  );
};

const ListHeader = ({
  isFilterVisible,
  setIsFilterVisible,
  searchValue,
  setSearchValue,
  actions,
  columnStates,
  fetchItems,
  setColumnStates,
  currentColor,
  loading,
}) => {
  return (
    <div className='flex items-center justify-between gap-2 p-2 bg-white border-b'>
      <div className='flex items-center gap-2'>
        <button
          onClick={() => setIsFilterVisible(!isFilterVisible)}
          className={`px-3 py-1.5 border rounded ${isFilterVisible ? 'bg-gray-100' : 'bg-white'} hover:bg-gray-50`}
        >
          Фильтр
        </button>
        <input
          type='text'
          placeholder='Поиск...'
          className='max-w-sm px-3 py-1.5 border rounded'
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={(e) => (e.key === 'Enter' || e.target.value == '' ) && fetchItems()}
        />
      </div>
      <div className='flex items-center gap-2'>
        <Tooltip
          content={
            <ActionsMenu
              actions={actions}
              loading={loading}
            />
          }
          trigger='click'
          className='px-4 py-2'
        >
          <button
            className='px-4 py-2 text-white rounded'
            style={{ backgroundColor: currentColor }}
          >
            Действия
          </button>
        </Tooltip>
        <Tooltip
          content={
            <ColumnToggler
              columnStates={columnStates}
              setColumnStates={setColumnStates}
            />
          }
          trigger='click'
          className='px-4 py-2'
        >
          <Settings />
        </Tooltip>
      </div>
    </div>
  );
};

const ListFilters = ({
  isFilterVisible,
  filterOptions,
  filters,
  handleFilterChange,
  clearFilters,
  fetchItems,
  currentColor,
}) => {
  if (!isFilterVisible) return null;

  return (
    <div className='w-full bg-gray-100 p-4 space-y-4 border rounded-md shadow-sm'>
        {/* Filter Controls */}
        <div className='flex gap-2'>
            <button
                className='px-3 py-1.5 text-white rounded'
                onClick={fetchItems}
                style={{ backgroundColor: currentColor }}
            >
                Найти
            </button>
            <button 
                className='px-3 py-1.5 bg-white border rounded hover:bg-gray-50' 
                onClick={clearFilters}
            >
                Очистить
            </button>
        </div>

        {/* Rows */}
        <div className='grid grid-cols-4 gap-4'>
            {filterOptions.map(({ key, label, field: FieldComponent }) => (
            <div key={key} className='space-y-1'>
                <label className='text-sm text-gray-600'>{label}</label>
                <FieldComponent
                    value={filters[key]}
                    onChange={(value) => handleFilterChange(key, value)}
                    className='w-full h-8 px-2 border rounded'
                />
            </div>
            ))}
        </div>
      
    </div>
  );
};

const ListTable = ({ items, columnStates, handleSelect, getSortIcon, handleSort }) => {
  const topScrollRef = useRef(null);
  const tableScrollRef = useRef(null);
  const [tableWidth, setTableWidth] = useState('100%');

  useEffect(() => {
    const tableContainer = tableScrollRef.current;
    if (!tableContainer) return;
    setTableWidth(tableContainer.scrollWidth || '100%');
  }, [items, columnStates]);

  useEffect(() => {
    const top = topScrollRef.current;
    const bottom = tableScrollRef.current;
    if (!top || !bottom) return undefined;

    const handleTopScroll = () => {
      if (bottom.scrollLeft !== top.scrollLeft) {
        bottom.scrollLeft = top.scrollLeft;
      }
    };

    const handleBottomScroll = () => {
      if (top.scrollLeft !== bottom.scrollLeft) {
        top.scrollLeft = bottom.scrollLeft;
      }
    };

    top.addEventListener('scroll', handleTopScroll);
    bottom.addEventListener('scroll', handleBottomScroll);

    return () => {
      top.removeEventListener('scroll', handleTopScroll);
      bottom.removeEventListener('scroll', handleBottomScroll);
    };
  }, [items, columnStates]);

  console.log('ListTable items:', items);
  return (
    <div className='space-y-2'>
      <div ref={topScrollRef} className='overflow-x-auto'>
        <div style={{ width: tableWidth, height: 1 }} />
      </div>
      <div
        ref={tableScrollRef}
        className='overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <table className='min-w-full divide-y divide-gray-200'>
          <thead>
            <tr>
              {columnStates.map(({ key, label, hide }) =>
                !hide && (
                  <th
                    key={key}
                    onClick={() => handleSort(key)}
                    className='px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider cursor-pointer'
                  >
                    <span className='flex items-center'>
                      {label}
                      {getSortIcon(key)}
                    </span>
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody className='bg-white divide-y divide-gray-200'>
            {items.map((item, index) => (
              <tr
                key={item.id || index}
                onClick={() => handleSelect(item)}
                className='hover:bg-gray-100 cursor-pointer'
              >
                {columnStates.map(({ key, component, hide }) =>
                  !hide && (
                    <td key={key} className='px-6 py-4 text-sm text-gray-500'>
                      {component ? component(item) : item[key]}
                    </td>
                  )
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Pagination = ({ currentPage, totalPages, totalCount, handlePageChange, limit, handleLimitChange, loading }) => {
  return (
    <div className='flex items-center justify-between px-4'>
            <div className='flex items-center space-x-2 text-sm text-gray-700'>
              <span>Показать:</span>
              <select
                value={limit}
                onChange={handleLimitChange}
                className='border border-gray-300 rounded px-2 py-1'
                disabled={loading}
              >
                {[10, 20, 50, 100].map(value => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
              <span>из {totalCount} записей</span>
            </div>
    
            <div className='flex items-center space-x-2'>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className='p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <ChevronLeft className='w-5 h-5' />
              </button>
              
              <div className='flex items-center space-x-1'>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                      className={`px-3 py-1 rounded ${
                        currentPage === pageNum
                          ? 'bg-gray-900 text-white'
                          : 'hover:bg-gray-100'
                      } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
    
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className='p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <ChevronRight className='w-5 h-5' />
              </button>
            </div>
          </div>
  );
};

const List = forwardRef(({ 
  targetUrl, 
  filterOptions,
  columns, 
  actions: customActions = []
 }, ref) => {
  const [filters, setFilters] = useState(
    filterOptions.reduce((acc, option) => {
      acc[option.key] = option.initialValue;
      return acc;
    }, {})
  );
  const [searchValue, setSearchValue] = useState('');
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [items, setItems] = useState([]);
  const [columnStates, setColumnStates] = useState(columns);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
  const { openModal, isLoading, setIsLoading } = useLayout();
  const { token, currentColor, showToast } = useStateContext();

  const handleSelect = (item) => {
    openModal({...item, refreshList: fetchItems});
  };

  const constructQueryParams = () => {
    const queryParams = [];

    if (sortConfig.key) {
      queryParams.push(`order=${sortConfig.key},${sortConfig.direction}`);
    }
    if (searchValue) {
      queryParams.push(`search=${searchValue}`);
    }
    const filterParams = [];
    filterOptions.forEach(({ key }) => {
      if (filters[key]) {
        if (Array.isArray(filters[key])) {
          if (filters[key].length > 0) {
            filterParams.push(`${key}=[${filters[key].map((item) => item.id).join(',')}]`);
          }
        } else {
          filterParams.push(`${key}=${filters[key]}`);
        }
      }
    });

    if (filterParams.length > 0) {
      queryParams.push(`filter=${filterParams.join(';')}`);
    }

    queryParams.push(`offset=${offset}`, `limit=${limit}`);

    return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
  };

  const fetchItems = async () => {
    try {
      setIsLoading(true);
      const queryString = constructQueryParams();
      const response = await loadList(targetUrl, queryString, token);
      console.log('fetchItems response:', response);
      setItems(response.rows);
      setTotalCount(response.meta.size);
      console.log('fetchItems items:', items);
      console.log('fetchItems totalCount:', totalCount);
    } catch (error) {
      showToast({ 
        title: 'Error!', 
        content: error.message || 'Could not fetch data.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Expose refresh method via ref
  useImperativeHandle(ref, () => ({
    refresh: fetchItems
  }));

  const exportItems = async () => {
    try {
      setIsLoading(true);
      const queryString = constructQueryParams();
      await exportList(`${targetUrl}/export`, queryString);
      showToast({
        title: 'Success!',
        content: 'Export started successfully.',
        type: 'success'
      });
    } catch (error) {
      console.log('Error exporting items:', error);
      showToast({
        title: 'Error!',
        content: error.message || 'Could not export data.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Build actions list with export as default
  const defaultExportAction = {
    key: 'export',
    label: 'Экспорт',
    action: exportItems,
  };

  const allActions = [
    defaultExportAction,
    ...customActions.map((action) => ({
      ...action,
      action: () => action.action(constructQueryParams(), token, { showToast, setIsLoading }),
    })),
  ];

  useEffect(() => {
    fetchItems();
  }, [offset, limit, sortConfig]);

  const handleFilterChange = (key, value) => {
    console.log('handleFilterChange', key, value);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters(
      filterOptions.reduce((acc, option) => {
        acc[option.key] = option.initialValue;
        return acc;
      }, {})
    );
    setSearchValue('');
    setOffset(0);
    fetchItems();
  };

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const totalPages = Math.ceil(totalCount / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  const handlePageChange = (newPage) => {
    setOffset((newPage - 1) * limit);
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setOffset(0);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className='w-4 h-4 ml-1' />;
    return sortConfig.direction === 'desc' ? (
      <ArrowDown className='w-4 h-4 ml-1' />
    ) : (
      <ArrowUp className='w-4 h-4 ml-1' />
    );
  };

  return (
    <div className=' p-4 space-y-4'>
      <ListHeader
        isFilterVisible={isFilterVisible}
        setIsFilterVisible={setIsFilterVisible}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        actions={allActions}
        columnStates={columnStates}
        fetchItems={fetchItems}
        setColumnStates={setColumnStates}
        currentColor={currentColor}
        loading={isLoading}
      />
      <ListFilters
        isFilterVisible={isFilterVisible}
        filterOptions={filterOptions}
        filters={filters}
        handleFilterChange={handleFilterChange}
        clearFilters={clearFilters}
        fetchItems={fetchItems}
        currentColor={currentColor}
      />
      <ListTable
        items={items}
        columnStates={columnStates}
        handleSelect={handleSelect}
        getSortIcon={getSortIcon}
        handleSort={handleSort}
      />
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        handlePageChange={handlePageChange}
        limit={limit}
        handleLimitChange={handleLimitChange}
        loading={isLoading}
      />
    </div>
  );
});

export default List;